import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../../hooks/useAuth';
import { screenAccessApi } from '../api/screenAccessApi';
import { screenAccessQueryKeys } from '../queryKeys';
import type { RoleAccessUpdate, ScreenKey } from '../model/types';

/**
 * What the signed-in user may do on a screen. Refetched when the window regains focus, so a
 * change the Super Admin saves reaches an open tab without a reload. The server checks every
 * write again, so this only decides what the screen offers.
 */
export const useMyScreenAccess = (screen: ScreenKey) => {
  const { companyId, roleId, isAuthenticated } = useAuth();
  const company = Number(companyId) || 0;
  return useQuery({
    queryKey: [...screenAccessQueryKeys.mine(screen, company), roleId ?? 0],
    queryFn: () => screenAccessApi.getMine(screen, company),
    enabled: isAuthenticated && company > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

export const useScreenAccessMatrix = (screen: ScreenKey, enabled = true) => {
  const { companyId } = useAuth();
  const company = Number(companyId) || 0;
  return useQuery({
    queryKey: screenAccessQueryKeys.matrix(screen, company),
    queryFn: () => screenAccessApi.getMatrix(screen, company),
    enabled: enabled && company > 0,
  });
};

/** Saves the rules; the page is refreshed from the reply and every "mine" query is dropped. */
export const useSaveScreenAccess = (screen: ScreenKey) => {
  const { companyId } = useAuth();
  const company = Number(companyId) || 0;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roles: RoleAccessUpdate[]) => screenAccessApi.saveMatrix(screen, company, roles),
    retry: 0,
    onSuccess: (matrix) => {
      queryClient.setQueryData(screenAccessQueryKeys.matrix(screen, company), matrix);
      void queryClient.invalidateQueries({ queryKey: [...screenAccessQueryKeys.all, 'mine', screen] });
    },
  });
};
